import dayjs from "dayjs";
import { AnimatePresence } from "framer-motion";
import { capitalize, findKey, forEach, get, isArray, isEmpty } from "lodash";
import { Children, useReducer } from "react";
import { Trans, withTranslation } from "react-i18next";

import { settings } from "@krea/customer-features/settings";
import { Error } from "@krea/customer-features/shared-components/fields/error";
import Fieldset from "@krea/customer-features/shared-components/fields/fieldset";
import FileItem from "@krea/customer-features/shared-components/fields/file-item";
import { FileInput } from "@krea/customer-features/shared-components/fields/file/index";
import FilesList from "@krea/customer-features/shared-components/fields/files-list";
import Radio from "@krea/customer-features/shared-components/fields/radio";
import RadioGroup from "@krea/customer-features/shared-components/fields/radiogroup";
import { Select } from "@krea/customer-features/shared-components/fields/select/";
import { Helper } from "@krea/customer-features/shared-components/helper";
import { Text } from "@krea/customer-features/shared-components/text";
import { useGetCachedCurrentLoanApplication } from "@krea/customer-features/store/current-loan-application/hooks";
import {
  useDeleteFile,
  usePostFieldRequest,
  usePutAccountingIntegrationData,
} from "@krea/customer-features/store/field-requests/hooks/mutations";
import { useGetOneFieldRequest } from "@krea/customer-features/store/field-requests/hooks/queries";
import { COUNTRY_CODE } from "@krea/customer-features/utils";

import AccountingIntegrationApp from "../AccountingIntegrationApp";
import { ErpSystemsList } from "../ErpSystemsList";
import FieldRequestDelegateForm from "../FieldRequestDelegateForm";
import FieldRequestPreviewHeader from "../FieldRequestPreviewHeader";
import FieldRequestSaved from "../FieldRequestSaved";

export const INIT_TYPE = {
  FORTNOX: "FORTNOX",
  VISMA_E_ECONOMY: "VISMA_E_ECONOMY",
  FILES: "FILES",
  DELEGATE: "DELEGATE",
};

const SELECTED_CHOICE = {
  AUTOMATIC: "AUTOMATIC",
  MANUALLY: "MANUALLY",
  DELEGATE: "DELEGATE",
};

const getDesiredAccountingDataTimeframe = () => {
  const dateFormat = "MMMM YYYY";
  const timePeriodTo = dayjs().subtract(2, "month").format(dateFormat);

  return {
    to: timePeriodTo,
  };
};

const AccountingDataForm = ({
  fieldRequestID,
  isPreviewMode,
  t,
  isDelegated,
  withInputLabel,
  initType,
  hasParentFieldRequest,
}) => {
  const { data: currentLoanApplication = {} } =
    useGetCachedCurrentLoanApplication();
  const { data: fieldRequest = {} } = useGetOneFieldRequest(
    fieldRequestID,
    isDelegated,
  );

  const { mutate: postFieldRequest, error } =
    usePostFieldRequest(fieldRequestID);
  const { mutate: putAccountingIntegrationData } =
    usePutAccountingIntegrationData();
  const { mutate: deleteFile } = useDeleteFile();

  const applyingCompanyID = currentLoanApplication?.applyingCompany?.id;
  const {
    displayName,
    submittedData,
    isSubmitted,
    delegatedInformationResources,
  } = fieldRequest;
  const countryCode = settings.countryCode;

  const files = submittedData?.files || [];
  const hasAutomationConsent =
    submittedData?.user_consented_integration || false;
  const errors = error?.data?.errors || [];

  let erpSystemInit = "";
  let selectedChoiceInit = "";

  if (
    !isDelegated &&
    !isSubmitted &&
    delegatedInformationResources?.length > 0
  ) {
    selectedChoiceInit = SELECTED_CHOICE.DELEGATE;
  }

  if (isDelegated && countryCode !== COUNTRY_CODE.SE) {
    selectedChoiceInit = SELECTED_CHOICE.MANUALLY;
  }

  if (submittedData) {
    erpSystemInit = findKey(
      ErpSystemsList,
      (o) => o.backendName === submittedData.erp_system,
    );
  }

  if (submittedData && Object.keys(submittedData).length > 0) {
    if (submittedData.user_consented_integration) {
      selectedChoiceInit = SELECTED_CHOICE.AUTOMATIC;
    } else if (submittedData.files && submittedData.files.length > 0) {
      selectedChoiceInit = SELECTED_CHOICE.MANUALLY;
    }
  }

  if (initType) {
    switch (initType) {
      case INIT_TYPE.FORTNOX:
        selectedChoiceInit = SELECTED_CHOICE.AUTOMATIC;
        erpSystemInit = "fortnox";
        break;
      case INIT_TYPE.VISMA_E_ECONOMY:
        selectedChoiceInit = SELECTED_CHOICE.AUTOMATIC;
        erpSystemInit = "visma";
        break;
      case INIT_TYPE.FILES:
        selectedChoiceInit = SELECTED_CHOICE.MANUALLY;
        break;
      case INIT_TYPE.DELEGATE:
        selectedChoiceInit = SELECTED_CHOICE.DELEGATE;
        break;
      default:
        break;
    }
  }

  function reducer(state, action) {
    switch (action.type) {
      case "toggleRadioButton":
        return {
          ...state,
          erpSystem: action.payload.erpSystem,
          isSupportedByInHouseAccountingIntegration:
            action.payload.isSupportedByInHouseAccountingIntegration,
          selectedChoice: action.payload.selectedChoice,
        };
      case "setErpSystem":
        return {
          ...state,
          isSupportedByInHouseAccountingIntegration:
            action.payload.isSupportedByInHouseAccountingIntegration,
          erpSystem: action.payload.erpSystem,
        };
      default:
        throw new Error();
    }
  }

  const [state, setState] = useReducer(reducer, {
    erpSystem: erpSystemInit,
    selectedChoice: selectedChoiceInit,
    isSupportedByInHouseAccountingIntegration:
      erpSystemInit &&
      ErpSystemsList[erpSystemInit].isSupportedByInHouseAccountingIntegration,
  });

  const { isSupportedByInHouseAccountingIntegration, erpSystem } = state;

  const showDropzone = state.selectedChoice === SELECTED_CHOICE.MANUALLY;

  const { to: timePeriodTo } = getDesiredAccountingDataTimeframe();

  const setErpSystem = (value) => {
    let isSupportedByInHouseAccountingIntegration = false;

    if (value) {
      const erpSystemInfo = ErpSystemsList[value];

      if (erpSystemInfo.isSupportedByInHouseAccountingIntegration) {
        isSupportedByInHouseAccountingIntegration =
          erpSystemInfo.isSupportedByInHouseAccountingIntegration;
      }
    }

    setState({
      type: "setErpSystem",
      payload: {
        isSupportedByInHouseAccountingIntegration:
          isSupportedByInHouseAccountingIntegration,
        erpSystem: value,
      },
    });
  };

  const deleteFileFunc = (fileID) => {
    deleteFile({ fileID, fieldRequestID, isDelegated });
  };

  const createFormData = (value) => {
    const formData = new FormData();

    if (isArray(value.files) && !isEmpty(value.files)) {
      forEach(value.files, (file) => {
        formData.append("files", file, file.name);
      });
    }
    formData.append("erp_system", value.erpSystem);

    return formData;
  };

  const postDataManually = (currentFiles) => {
    let erpSystemToSend = "OTHER";

    if (erpSystem) {
      erpSystemToSend = ErpSystemsList[erpSystem].backendName;
    }

    const payload = {
      erpSystem: erpSystemToSend,
      files: currentFiles || files,
    };

    if (payload.files && payload.files.length) {
      const formData = createFormData(payload);
      postFieldRequest(formData);
    }
  };

  const inHouseAccountingErrorCallback = (error) => {
    console.log("error in inhouse accounting app: ", error);
  };

  const inHouseAccountingSuccessCallback = ({
    accountingDataId,
    organisationNumber,
  }) => {
    putAccountingIntegrationData({
      accountingDataId,
      organisationNumber,
      serviceType: "ACCOUNTING_INTEGRATION",
      fieldRequestID,
      isDelegated,
    });
  };

  const toggleRadioButton = ({ target }) => {
    const { value } = target;

    let erpSystem = "";

    if (value === SELECTED_CHOICE.AUTOMATIC && !erpSystem) {
      // default to FORTNOX
      erpSystem = "fortnox";
    }

    setState({
      type: "toggleRadioButton",
      payload: {
        erpSystem: erpSystem,
        isSupportedByInHouseAccountingIntegration:
          erpSystem &&
          ErpSystemsList[erpSystem].isSupportedByInHouseAccountingIntegration,
        selectedChoice: value,
      },
    });
  };

  const supportedErpList = Object.keys(ErpSystemsList).filter(
    (key) => ErpSystemsList[key].isSupportedByInHouseAccountingIntegration,
  );

  return (
    <>
      {isPreviewMode ? (
        <>
          <FieldRequestPreviewHeader
            label={displayName}
            value={
              submittedData &&
              `${t("iaq.accountingData.previewLabel")}: ${
                ErpSystemsList &&
                ErpSystemsList[erpSystem] &&
                ErpSystemsList[erpSystem].name
              }`
            }
          />
          {files && !!files.length && (
            <FieldRequestPreviewHeader
              subheader
              label={t("iaq.uploadedFiles")}
              value={
                <>
                  {files.length > 0 && (
                    <AnimatePresence>
                      {Children.toArray(
                        files.map((file) => {
                          return (
                            <FileItem
                              file={file}
                              deleteFile={deleteFileFunc}
                              isPreviewMode={isPreviewMode}
                            />
                          );
                        }),
                      )}
                    </AnimatePresence>
                  )}
                </>
              }
            />
          )}
        </>
      ) : (
        <>
          <form className="input-form" onSubmit={(e) => e.preventDefault()}>
            <Fieldset>
              {/* Conditional Radio elements inside RadioGroup didnt work for some reason */}
              {!hasParentFieldRequest ? (
                isDelegated ? (
                  <>
                    {countryCode === COUNTRY_CODE.SE && (
                      <RadioGroup
                        disabled={hasAutomationConsent}
                        value={state.selectedChoice}
                        onChange={toggleRadioButton}
                      >
                        <Radio
                          value={SELECTED_CHOICE.AUTOMATIC}
                          id="accounting-automatic"
                          name="accounting-radio-button"
                          checked={
                            SELECTED_CHOICE.AUTOMATIC === state.selectedChoice
                          }
                          label={t("iaq.accountingData.automaticFetching")}
                        />

                        <Radio
                          value={SELECTED_CHOICE.MANUALLY}
                          id="accounting-manually"
                          name="accounting-radio-button"
                          label={t("iaq.accountingData.uploadManually")}
                          checked={
                            SELECTED_CHOICE.MANUALLY === state.selectedChoice
                          }
                        />
                      </RadioGroup>
                    )}
                  </>
                ) : (
                  <RadioGroup
                    disabled={hasAutomationConsent}
                    value={state.selectedChoice}
                    onChange={toggleRadioButton}
                  >
                    {countryCode === COUNTRY_CODE.SE ? (
                      <Radio
                        value={SELECTED_CHOICE.AUTOMATIC}
                        id="accounting-automatic"
                        name="accounting-radio-button"
                        checked={
                          SELECTED_CHOICE.AUTOMATIC === state.selectedChoice
                        }
                        label={t("iaq.accountingData.automaticFetching")}
                      />
                    ) : (
                      <></>
                    )}

                    <Radio
                      value={SELECTED_CHOICE.MANUALLY}
                      id="accounting-manually"
                      name="accounting-radio-button"
                      label={t("iaq.accountingData.uploadManually")}
                      checked={
                        SELECTED_CHOICE.MANUALLY === state.selectedChoice
                      }
                    />

                    <Radio
                      value={SELECTED_CHOICE.DELEGATE}
                      id="accounting-delegate"
                      name="accounting-radio-button"
                      label={t("iaq.accountingData.delegateChoice")}
                      checked={
                        SELECTED_CHOICE.DELEGATE === state.selectedChoice
                      }
                    />
                  </RadioGroup>
                )
              ) : null}

              {!hasParentFieldRequest &&
                state.selectedChoice === SELECTED_CHOICE.AUTOMATIC &&
                supportedErpList.length > 1 && (
                  <Select
                    id="accounting"
                    label={withInputLabel && <>{displayName}</>}
                    value={state.erpSystem}
                    disabled={hasAutomationConsent}
                    onChange={(e) => {
                      setErpSystem(e.target.value);
                    }}
                  >
                    <option value="" disabled>
                      {t("iaq.accountingData.chooseAccoutningSystem")}
                    </option>
                    {Children.toArray(
                      supportedErpList.map((key) => (
                        <option value={key}>{ErpSystemsList[key].name}</option>
                      )),
                    )}
                  </Select>
                )}

              {state.selectedChoice === SELECTED_CHOICE.AUTOMATIC &&
                isSupportedByInHouseAccountingIntegration &&
                !hasAutomationConsent && (
                  <AccountingIntegrationApp
                    onSuccess={inHouseAccountingSuccessCallback}
                    onFailure={inHouseAccountingErrorCallback}
                    applyingCompanyID={applyingCompanyID}
                    erpSystem={erpSystem}
                    hasParentFieldRequest={hasParentFieldRequest}
                  />
                )}

              {hasAutomationConsent && (
                <Text className="font-weight-bold">
                  {t("iaq.accountingData.automaticFetchingComplete")}
                </Text>
              )}

              {(showDropzone || files.length > 0) && (
                <>
                  <Text
                    size="sm"
                    className="d-flex flex-wrap font-weight-bold text-warning"
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    <Trans
                      i18nKey="iaq.accountingData.accountingManually.periodText"
                      components={{
                        span: <span />,
                        helper: (
                          <Helper
                            className="ml-1 mt-n1"
                            content={t(
                              "iaq.accountingData.accountingManually.helper",
                            )}
                          />
                        ),
                        to: capitalize(timePeriodTo),
                      }}
                    />
                  </Text>

                  {showDropzone && (
                    <FileInput
                      multiple
                      id="balance-sheet"
                      files={files}
                      postFiles={postDataManually}
                      error={get(errors, "[0].message", null)}
                    />
                  )}

                  {files.length > 0 && (
                    <FilesList
                      className={hasParentFieldRequest ? "mb-3" : ""}
                      label={t("iaq.uploadedFiles")}
                    >
                      <AnimatePresence>
                        {Children.toArray(
                          files.map((file) => {
                            return (
                              <FileItem
                                file={file}
                                deleteFile={deleteFileFunc}
                                isPreviewMode={isPreviewMode}
                              />
                            );
                          }),
                        )}
                      </AnimatePresence>
                    </FilesList>
                  )}
                </>
              )}

              {!isDelegated &&
                state.selectedChoice === SELECTED_CHOICE.DELEGATE && (
                  <FieldRequestDelegateForm fieldRequestID={fieldRequestID} />
                )}
            </Fieldset>
          </form>

          {!hasParentFieldRequest && (
            <FieldRequestSaved fieldRequestID={fieldRequestID} />
          )}
        </>
      )}

      {errors.map((error, index) => (
        <Error className="mt-3" key={index}>
          {error.message}
        </Error>
      ))}
    </>
  );
};

export default withTranslation()(AccountingDataForm);
